import { PropsWithChildren, Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { Navigate, RouteObject, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import yn from "yn";
import AppHeader, { HEADER_HEIGHT } from "~/components/AppHeader";
import Loading from "~/components/Loading";
import { ManifoldNextGenLayout } from "~/components/ManifoldNextGenLayout";
import useAppHeader from "~/contexts/header";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import {
  Admin,
  Cohorts,
  DataBrowser,
  DataOverview,
  Home,
  NotFound,
  ResearchWorkbench,
  Shared,
  SmPortal,
  ShadcnTest,
} from "~/pages";
import { AgentDetail } from "~/pages/manifold-nextgen/components/agents/AgentDetail";
import { Agents } from "~/pages/manifold-nextgen/components/agents/Agents";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { ProtectedAppRoute } from "./components/ProtectedAppRoute";
import redirects from "./redirects";
import { ROUTE_404 } from "./router/constants";
import { VIEW_SM_APP } from "./router/permissions";

interface AppLayoutProps extends PropsWithChildren {
  enabled?: boolean;
}

const {
  VITE_RWB_ENABLED,
  VITE_SM_ENABLED,
  VITE_OUTREACH_API,
  VITE_PARTICIPANT_API,
  VITE_SURVEY_API,
  VITE_DATA_DASHBOARD_URL,
  VITE_ADMIN_APP_ENABLED,
  VITE_COHORT_EXPLORER_ENABLED,
} = import.meta.env;

/**
 * Top-level app layout
 * @param enabled - Build-time, env specific feature flag support
 * @param children
 * @constructor
 */
const AppLayout = ({ enabled = true, children }: AppLayoutProps) => {
  const location = useLocation();
  const wrapperRef = useRef<HTMLElement>(null);
  const { setCenterContent } = useAppHeader();
  const contentHeight = `calc(100vh - ${HEADER_HEIGHT})`;
  const [preferences] = useUserPreferences();

  useEffect(() => {
    // Handle resetting scroll on navigation
    wrapperRef.current?.scrollTo({ top: 0 });
  }, [wrapperRef.current, location.pathname]);

  useLayoutEffect(() => {
    // Ugly hack to avoid flicker between tab switches on participant profile
    // TODO: Deprecate in favor of a more generic solution for multi-page center content
    if (!/^\/sm\/participants\/\d+.*$/.test(location.pathname)) {
      // Reset page specific center content when navigating
      setCenterContent(null);
    }
  }, [location.pathname, setCenterContent]);

  if (!enabled) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (preferences.optedIntoManifoldNextGen && !location.pathname.startsWith(ROUTE_NEXTGEN_VIEW)) {
    return <Navigate to={`${ROUTE_NEXTGEN_VIEW}/agents`} replace />;
  }

  return (
    <QueryParamProvider adapter={ReactRouter6Adapter} options={{ removeDefaultsFromUrl: true }}>
      <AppHeader />
      <Box
        component="main"
        ref={wrapperRef}
        sx={{
          position: "relative",
          height: contentHeight,
          overflow: "auto",
          bgcolor: "common.pageBackground",
        }}
      >
        {children}
      </Box>
    </QueryParamProvider>
  );
};

/**
 * Wrapper for lazy imported apps
 * @param children - Route content which might also include its own sub-router
 * @constructor
 */
const LazyWrapper = ({ children }: PropsWithChildren) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

const routes: RouteObject[] = [
  {
    index: true,
    element: (
      <AppLayout>
        <Home />
      </AppLayout>
    ),
  },
  ...redirects,
  {
    path: "shadcn-test",
    element: (
      <AppLayout enabled={import.meta.env.DEV}>
        <ShadcnTest />
      </AppLayout>
    ),
  },
  // Note: Global files browser is now under /data-browser/files
  // The redirect from /files to /data-browser/files is in redirects.tsx
  {
    path: "cohorts/*",
    element: (
      <AppLayout enabled={yn(VITE_COHORT_EXPLORER_ENABLED, { default: false })}>
        <LazyWrapper>
          <Cohorts />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "workbenches/*",
    element: (
      <AppLayout enabled={yn(VITE_RWB_ENABLED, { default: false })}>
        <LazyWrapper>
          <ResearchWorkbench />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "data-browser/*",
    element: (
      <AppLayout>
        <DataBrowser />
      </AppLayout>
    ),
  },
  {
    path: "data-overview",
    element: (
      <AppLayout enabled={!!VITE_DATA_DASHBOARD_URL}>
        <DataOverview />
      </AppLayout>
    ),
  },
  {
    path: "sm/*",
    element: (
      <AppLayout enabled={yn(VITE_SM_ENABLED, { default: false })}>
        <LazyWrapper>
          <ProtectedAppRoute permission={VIEW_SM_APP}>
            <SmPortal />
          </ProtectedAppRoute>
        </LazyWrapper>
      </AppLayout>
    ),
    handle: {
      serverHealth: [
        {
          endpoint: VITE_OUTREACH_API,
          label: "Outreach Service",
        },
        {
          endpoint: VITE_PARTICIPANT_API,
          label: "Participant Service",
        },
        {
          endpoint: VITE_SURVEY_API,
          label: "Survey Service",
        },
      ],
    },
  },
  {
    path: "admin/*",
    element: (
      <AppLayout enabled={yn(VITE_ADMIN_APP_ENABLED, { default: false })}>
        <LazyWrapper>
          <Admin />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "shared/*",
    element: (
      <AppLayout>
        <LazyWrapper>
          <Shared />
        </LazyWrapper>
      </AppLayout>
    ),
  },
  {
    path: "nextgen",
    element: <ManifoldNextGenLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/nextgen/agents" replace />,
      },
      {
        path: "agents",
        element: <Agents />,
      },
      {
        path: "agents/:agentId",
        element: <AgentDetail />,
      },
    ],
  },
  { path: "404", element: <NotFound /> },
  { path: "*", element: <Navigate to={ROUTE_404} replace /> },
];

export default routes;
